import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth.jsx";
import TankShell from "./layouts/TankShell";
import Overview from "./pages/Overview";
import Edit from "./pages/Edit";
import Matches from "./pages/Matches";
import Manage from "./pages/Manage";
import Statistics from "./pages/Statistics";
import Me from "./pages/Me";
import Login from "./pages/Login";
import AppPrivacy from "./pages/AppPrivacy";
import AppSupport from "./pages/AppSupport";
import { getFtpStatus } from "./api";

const allowedRoutes = ["/login", "/overview", "/edit", "/matches", "/manage", "/training", "/statistics", "/me"];
const FTP_PREF_KEY = "matrix-auto-ftp-enabled";

const normalizePath = (path) => {
  const clean = (path || "/").replace(/\/+$/, "") || "/";
  if (clean === "/") return "/overview";
  return clean;
};

const ensureAllowed = (path) => {
  const normalized = normalizePath(path);
  if (allowedRoutes.includes(normalized)) return normalized;
  // Tank Redesign sub-routes: /manage/training, /manage/teams,
  // /manage/persons, /manage/unlinked, /manage/company/<sub>, etc.
  // The Manage page reads window.location.pathname directly to resolve
  // its sub-section, so we just need to keep the URL intact and render
  // the parent route.
  if (normalized.startsWith("/manage/")) return normalized;
  return "/overview";
};

const publicPages = {
  "/app/privacy": AppPrivacy,
  "/app/support": AppSupport,
};

export default function App() {
  const pagePath = normalizePath(window.location.pathname);
  const PublicPage = publicPages[pagePath];
  if (PublicPage) return <PublicPage />;

  return <OperatorApp />;
}

function OperatorApp() {
  const [path, setPath] = useState(() => ensureAllowed(window.location.pathname));
  const { isAuthenticated, logout, login, loading, user } = useAuth();
  const [topBadge, setTopBadge] = useState("");

  // Auto-FTP state (temporary feature)
  const [autoFtp, setAutoFtp] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(FTP_PREF_KEY);
    return stored === "true";
  });
  const [ftpConfigured, setFtpConfigured] = useState(false);

  // Load FTP status on mount
  useEffect(() => {
    getFtpStatus()
      .then((status) => {
        setFtpConfigured(status.configured && status.enabled);
      })
      .catch(() => {
        setFtpConfigured(false);
      });
  }, []);

  // Persist auto-FTP preference
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(FTP_PREF_KEY, String(autoFtp));
  }, [autoFtp]);

  useEffect(() => {
    const handler = () => setPath(ensureAllowed(window.location.pathname));
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const navigate = (target) => {
    const next = ensureAllowed(target);
    if (next === path) return;
    window.history.pushState({}, "", next);
    setPath(next);
  };

  useEffect(() => {
    // Don't redirect while auth status is still being determined
    if (loading) return;

    if (!isAuthenticated && path !== "/login") {
      navigate("/login");
    } else if (isAuthenticated && path === "/login") {
      navigate("/overview");
    }
  }, [isAuthenticated, path, loading]);

  const content = useMemo(() => {
    switch (path) {
      case "/overview":
        return <Overview />;
      case "/edit":
        return <Edit />;
      case "/matches":
        return <Matches />;
      case "/manage":
        return <Manage />;
      case "/training":
        // Redirect to /manage#training for backwards compatibility
        window.history.replaceState({}, "", "/manage#training");
        return <Manage />;
      case "/statistics":
        return <Statistics />;
      case "/me":
        return <Me user={user} />;
      default:
        // /manage/<sub> sub-routes (e.g. /manage/training,
        // /manage/company/caption-builder) route to Manage; the page
        // reads window.location.pathname directly to resolve them.
        if (path.startsWith("/manage/")) return <Manage />;
        return <Overview />;
    }
  }, [path, user]);

  // Show nothing while checking auth status
  if (loading) {
    return null;
  }

  // Not authenticated - always show login
  if (!isAuthenticated) {
    return <Login onSuccess={() => navigate("/overview")} login={login} />;
  }

  return (
    <TankShell
      currentPath={path}
      onNavigate={navigate}
      onLogout={() => {
        logout();
        navigate("/login");
      }}
      topBadge={topBadge}
      user={user}
    >
      {React.isValidElement(content)
        ? React.cloneElement(content, {
            autoFtp,
            ftpConfigured,
            onToggleAutoFtp: () => setAutoFtp((v) => !v),
            onTopBadge: setTopBadge,
            onNavigate: (to, imageId, badge, filters) => {
              if (badge !== undefined) {
                setTopBadge(badge || "");
                return;
              }
              const target = ensureAllowed(to || "/overview");
              if (imageId) {
                let url = `${target}#image=${encodeURIComponent(imageId)}`;
                if (filters && Array.isArray(filters) && filters.length > 0) {
                  url += `&filter=${encodeURIComponent(filters.join(","))}`;
                }
                window.history.pushState({}, "", url);
                setPath(target);
                return;
              }
              navigate(target);
            },
          })
        : content}
    </TankShell>
  );
}
